import React, { useState } from 'react';
import { api } from '../api/nocode';
import { TABLES } from '../api/tables';
import Button from './ui/Button';
import Card from './ui/Card';
import Input from './ui/Input';
import SafeIcon from '../common/SafeIcon';

const CONTENT_FIELDS = [
  'day', 'block', 'is_active', 'day_title', 'persona_label', 'badge_color',
  'intro_text', 'video_url', 'audio_url', 'session_title', 'title', 'body',
  'zoom_link', 'session_description', 'checkin_prompt', 'unlock_time',
  'close_time', 'bonus_videos', 'video_embed', 'thumbnail_url',
  'product_headline', 'product_image_url', 'product_description', 'product_url',
  'product_button_text', 'counselor_subject', 'counselor_note', 'slides_url',
  'debrief_note', 'section_title', 'opening_paragraph', 'compass_prompts',
  'prompt', 'fb_link', 'encouragement_note'
];

function slugify(value) {
  return String(value || '').toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function shiftDate(value, sourceStart, targetStart) {
  if (!value || !sourceStart || !targetStart) return value;
  const source = new Date(`${sourceStart.slice(0, 10)}T00:00:00`);
  const target = new Date(`${targetStart.slice(0, 10)}T00:00:00`);
  const original = new Date(String(value).replace(' ', 'T'));
  if ([source, target, original].some((date) => Number.isNaN(date.getTime()))) return value;
  const shifted = new Date(original.getTime() + target.getTime() - source.getTime());
  return shifted.toISOString().slice(0, String(value).length >= 16 ? 16 : 10)
    .replace('T', String(value).includes(' ') ? ' ' : 'T');
}

async function readAll(table, runId) {
  const rows = [];
  for (let page = 1; page <= 100; page += 1) {
    const result = await api.getUnscoped(table, {
      run_id: String(runId), page, limit: 1000
    });
    const batch = Array.isArray(result?.data) ? result.data : [];
    rows.push(...batch);
    if (batch.length < 1000 || result?.metadata?.hasMore === false) return rows;
  }
  throw new Error(`Too many ${table} records to clone safely.`);
}

export default function CloneRun({ run, runs, sessions, onClose, onCloned }) {
  const [title, setTitle] = useState(`${run.title || run.name || 'Event Run'} Copy`);
  const [slug, setSlug] = useState('');
  const [startDate, setStartDate] = useState(run.start_date?.slice(0, 10) || '');
  const [endDate, setEndDate] = useState(run.end_date?.slice(0, 10) || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const clone = async () => {
    const targetSlug = slugify(slug || title);
    if (!title.trim() || !targetSlug || !startDate || !endDate || endDate < startDate) {
      setError('Enter a name, valid dates, and a URL slug.');
      return;
    }
    if (runs.some((item) => slugify(item.slug || item.title || item.name) === targetSlug)) {
      setError('That run URL slug is already in use.');
      return;
    }

    setBusy(true);
    setError('');
    let createdRunId = null;
    try {
      // Read the entire source before creating anything. Only curriculum tables
      // are included: no passes, enrollments, students, letters, or partners.
      const [content, evergreen] = await Promise.all([
        readAll(TABLES.content, run.id),
        readAll(TABLES.evergreenContent, run.id)
      ]);
      const sourceSession = sessions.find((item) => String(item.id) === String(run.session_id));
      const created = await api.postUnscoped(TABLES.runs, {
        session_id: String(run.session_id),
        title: title.trim(),
        slug: targetSlug,
        start_date: startDate,
        end_date: endDate,
        is_active: 0,
        status: 'inactive',
        skillplate_paid_product: '',
        skillplate_allaccess_product: '',
        skillplate_products: '[]'
      });
      createdRunId = created?.data?.id ?? created?.id;
      if (createdRunId == null) throw new Error('The new run was created but its ID was not returned.');

      for (const [table, rows] of [
        [TABLES.content, content],
        [TABLES.evergreenContent, evergreen]
      ]) {
        for (const row of rows) {
          const payload = Object.fromEntries(
            CONTENT_FIELDS.filter((field) => row?.[field] !== undefined)
              .map((field) => [field, row[field]])
          );
          payload.run_id = String(createdRunId);
          payload.session_id = String(run.session_id);
          payload.department_id = String(row.department_id || sourceSession?.department_id || '');
          payload.owner_id = 'admin';
          payload.unlock_time = shiftDate(payload.unlock_time, run.start_date, startDate);
          payload.close_time = shiftDate(payload.close_time, run.start_date, startDate);
          // A sent notification is not curriculum and must not carry over.
          payload.counselor_last_sent_at = null;
          await api.postUnscoped(table, payload);
        }
      }
      await onCloned();
      onClose();
    } catch (cause) {
      setError(`${cause?.message || 'Clone failed.'}${createdRunId != null
        ? ` Run ${createdRunId} may be partially copied and remains inactive; review its content before activating it.`
        : ''}`);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4">
      <Card className="my-8 w-full max-w-lg space-y-4 border-2 border-dark-green shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-dark-green">Clone Event Run</h3>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close">
            <SafeIcon name="X" />
          </button>
        </div>
        <p className="text-sm text-muted">
          Copies all run session content and podcast episodes into a new inactive run.
          Students, passes, enrollments, Study Hall responses, self-letters, and lab
          partners are not copied. Skillplate products must be assigned separately.
        </p>
        <Input label="New run name" value={title} onChange={(event) => setTitle(event.target.value)} />
        <Input label="New run URL slug" value={slug} onChange={(event) => setSlug(event.target.value)} placeholder={slugify(title)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Start date" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
          <Input label="End date" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </div>
        <p className="text-xs text-muted">
          Unlock and close times move by the difference between the source and new start dates.
        </p>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={clone} disabled={busy}>
            <SafeIcon name="Copy" /> {busy ? 'Cloning content...' : 'Clone Run'}
          </Button>
        </div>
      </Card>
    </div>
  );
}